import {
  DealExtraRestrictType,
  DEFAULT_RESTRICT_TYPE,
  isPackageRestricted,
  OrderValues,
  PackageType,
  RemoteConfig,
  StrapiID,
} from '@ca/common/types'
import {
  getDealVariant,
  useBaseSessionDeal,
  useDeal,
} from '@order/hooks/useDeal'
import useOrderValues from '@order/hooks/values/useOrderValues'

import useStepsState from '@order/hooks/useStepsState'
import { checkPackageCondition } from '@order/isPackageAvailable'
import checkOrderValues from '@order/utils/checkOrderValues'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import { useMemo } from 'react'

const hasValidPackages = ({
  packages,
  config,
  values,
  type,
  restrictType,
}: {
  type: PackageType | undefined
  restrictType?: DealExtraRestrictType
  config?: RemoteConfig | null
  packages: StrapiID[]
  values: OrderValues
}) => {
  if (!config) {
    return false
  }
  const vfCondition =
    config?.region.conditions?.vf || config?.region.vfCondition
  const isValidVf = vfCondition
    ? checkOrderValues(vfCondition, values, config)
    : true

  return packages.some((id) => {
    const pckg = config.packages[id]
    if (!pckg) {
      return false
    }
    const isRestricted = isPackageRestricted(type, pckg.type, restrictType)
    if (isRestricted) {
      return false
    }
    if (pckg.type !== 'sv' && !isValidVf) {
      return false
    }
    const res = checkPackageCondition(pckg, values)
    return res
  })
}

const useExtraDeal = () => {
  const config = useRemoteConfig()
  const base = useBaseSessionDeal()
  const baseDeal = useDeal(base?.dealId)
  const extraDeal = useDeal(baseDeal?.extra || undefined)
  const state = useStepsState()

  const orderValues = useOrderValues(state)

  const restrictType = extraDeal?.extraRestrictType || DEFAULT_RESTRICT_TYPE

  const isValidVF =
    extraDeal?.vfDeal &&
    !isPackageRestricted(
      orderValues.values.type as PackageType,
      'vf',
      restrictType
    )

  const resolvedDeal = useMemo(() => {
    if (!extraDeal) {
      return undefined
    }
    return getDealVariant(extraDeal, orderValues, config)
  }, [extraDeal, orderValues, config])

  return {
    deal: resolvedDeal,
    // Treat as pending order if we have a filter not a specific deal
    mode: extraDeal?.settings?.extra,
    isValid:
      hasValidPackages({
        type: orderValues.values.type as PackageType,
        restrictType,
        packages: extraDeal?.packages || [],
        config,
        values: orderValues,
      }) || isValidVF,
  }
}

export default useExtraDeal
